import ReactModal from 'react-modal'
import { X } from 'react-feather'
import classNames from 'classnames'
import { Trans } from '@lingui/macro'
import Button from '@components/atoms/button'
import CourseFilterCheckboxList from '@components/courses/course-filter-checkbox-list'
import { CourseFilterState, FilterOption } from '@lib/utils/course-filters'
import Modal from '@components/common/modal'

if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#__next')

export type CourseFiltersPanelDefinition = {
  key: keyof CourseFilterState
  label: string
  options: FilterOption[]
  twoColumn?: boolean
}

type CourseFiltersModalProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  filters: CourseFiltersPanelDefinition[]
  selectedValues: CourseFilterState
  activeKey: keyof CourseFilterState | null
  onActiveKeyChange: (key: keyof CourseFilterState) => void
  onToggle: (key: keyof CourseFilterState, value: string) => void
  onClearAll: () => void
}

const CourseFiltersModal = ({
  isOpen,
  setIsOpen,
  filters,
  selectedValues,
  activeKey,
  onActiveKeyChange,
  onToggle,
  onClearAll,
}: CourseFiltersModalProps) => {
  const activeFilter =
    filters.find((filter) => filter.key === activeKey) ?? filters[0]

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shouldCloseOnEsc
      width="w-full sm:max-w-3xl"
      contentPadding="p-2 sm:p-5"
      className="h-128 sm:h-96 flex flex-col select-none"
      animated
    >
      <div className="flex items-center justify-between pb-4 border-b border-grey-300 flex-shrink-0">
        <h2 className="text-lg font-600 text-grey-900">
          <Trans>Filters</Trans>
        </h2>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-grey-700 hover:text-orange focus:outline-none"
          aria-label="Close"
        >
          <X size={22} />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-24  sm:w-32 border-r border-grey-300 overflow-y-auto sm:flex-shrink-0">
          {filters.map((filter) => {
            const count = selectedValues[filter.key].length
            const isActive = activeFilter?.key === filter.key
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => onActiveKeyChange(filter.key)}
                className={classNames(
                  'flex items-center justify-between gap-2 w-full text-left px-2 py-3 text-sm border-l-2 focus:outline-none leading-tight',
                  isActive
                    ? 'bg-grey-100 border-orange text-orange font-600'
                    : 'border-transparent text-grey-900 hover:bg-grey-100'
                )}
              >
                <span>{filter.label}</span>
                {count > 0 && (
                  <span className="inline-flex items-center justify-center flex-shrink-0 rounded-full bg-orange text-white leading-none h-4 w-4 text-xs font-600">
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {activeFilter && (
            <CourseFilterCheckboxList
              name={activeFilter.label}
              options={activeFilter.options}
              selectedValues={selectedValues[activeFilter.key]}
              onToggle={(value) => onToggle(activeFilter.key, value)}
              twoColumn={activeFilter.twoColumn}
            />
          )}
        </div>
      </div>

      <div className="flex pt-4 gap-3 border-t border-grey-300 flex-shrink-0">
        <Button
          variant="outline"
          size="md"
          className="flex-1"
          onClick={onClearAll}
        >
          <Trans>Clear all</Trans>
        </Button>
        <Button
          variant="solid"
          size="md"
          className="flex-1"
          onClick={() => setIsOpen(false)}
        >
          <Trans>Apply</Trans>
        </Button>
      </div>
    </Modal>
  )
}

export default CourseFiltersModal
